"use client";
import Loading from "@/app/loading";
import { useCurrencyPrice } from "@/hooks";
import { getFromLocale, getSiteInfo, multiPhoneFormatter } from "@/utils";
import {
  CheckCircleIcon,
  DocumentDuplicateIcon,
  PhoneIcon,
} from "@heroicons/react/20/solid";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import { useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { useSelector } from "react-redux";
import { CountDown } from "./CountDown";
import { cleanPhoneNumber } from "./Footer";
import { HeroHeaderDefault } from "./HeroHeaderDefault";
import { HTMLWrapper } from "./HtmlWrapper";
import {HeaderBannerWithImage} from "@/components/HeaderBannerWithImage";

export const HeroHeaderCommon = ({ data, phone ,lang}) => {
  const t = useTranslations();
  const apiDate = new Date(data?.body?.end_date);
  const currentDateTime = new Date();
  const isActive = apiDate >= currentDateTime;
  const isSuccess = data?.body?.end_date ? true : false
  const scrollDown = () => {
    window.scrollTo({
      top: screen.height - 120,
      behavior: "smooth",
    });
  };
  const { curr: currency } = useSelector((state) => state.common) ?? "AZN";
  const cleanedPhoneNumber = cleanPhoneNumber(phone);
  const multiPhoneNumber = multiPhoneFormatter(phone);

  let endDate = data?.body["end_date"];
  let title = data?.body.title[lang];
  let description = data?.body.description[lang];
  let oldPrice = data?.body["old_price"];
  let newPrice = data?.body["new_price"];

  const [defaultHero, setDefaultHero] = useState(false);
  const [copySuccessful, setCopySuccessful] = useState(false);
  const navigateCategory = {
    domain: { url: "/domain", name: t("domain") },
    hosting: { url: "/web-hosting", name: t("web_hosting") },
    reseller: { url: "/dedicated-server", name: t("server") },
    vps: { url: "/vps-hosting", name: t("vps") },
    sayt: { url: "/cost-calculator", name: "" },
  };
  const isWebsiteCategory = data?.body?.category == "Sayt";

  const { url, name } =
    navigateCategory[data?.body?.category?.toLowerCase()] || {};
  const linkProps = isWebsiteCategory
    ? { href: `https://wa.me/${cleanedPhoneNumber}`, target: "__blank" }
    : { href: url };
  const openTawkToChat = () => {
    if (window.Tawk_API) {
      window.Tawk_API.toggle();
    }
  };
  const copyToClipboard = async (textToCopy) => {
    try {
      await navigator.clipboard.writeText(textToCopy);
      setCopySuccessful(true);
    } catch (err) {
      console.log("Error");
    }
  };
  const formattedOldPrice = useCurrencyPrice(oldPrice, currency);
  const formattedNewPrice = useCurrencyPrice(newPrice, currency);


  return !isActive || defaultHero ? (
    // <HeroHeaderDefault />
      <HeaderBannerWithImage bannerImageUrl={"/images/banner-flip-az.png"} />
  ) : isSuccess ? (
    <div className="lg:py-20 lg:pt-10 pt-0 lmd:py-8 md:py-6 py-4 min-h-[calc(100vh-90px)] relative flex justify-center items-center">
      <div className="max-w-7xl mx-auto sm:px-8 px-4 text-darkBlue2 ">
        <div className="grid grid-cols-1 lg:grid-cols-7-5 gap-x-6 lg:gap-x-12 mb-[15px]">
          <div>
            <h1 className="lg:text-title-52 md:text-title-xl sm:text-title-md2 text-title-sm xmd:font-extrabold font-bold xmd:mb-[16px] mb-[12px]">
              {title}
            </h1>
            <p className="md:text-2xl sm:text-xl text-lg font-semibold  mb-[20px] sm:mb-[16px]">
              {t("demand")}
            </p>
            <HTMLWrapper htmlString={description} classes={"vh-benefit"} />
            <div className="flex gap-x-[25px]">
              <s className="sm:text-title-sm sm:mt-[20px] mt-[12px] font-semibold">
                {isWebsiteCategory
                  ? `${formattedOldPrice}`
                  : `${formattedOldPrice}/${t("month")}`}
              </s>
              <div className="flex items-center font-bold">
                <strong className="xmd:text-[60px] sm:text-[52px] text-[36px] text-yellow-price">
                  {formattedNewPrice}
                </strong>
                {isWebsiteCategory ? null : (
                  <span className="text-title-sm">/{t("month")}</span>
                )}
              </div>
            </div>
            <p>
              {!isWebsiteCategory
                ? t("getServices", { name })
                : t("getWebsite")}
            </p>
            <div className="my-5 flex gap-x-5 flex-col lg:justify-start justify-center xmd2:flex-row gap-y-2 text-center">
              <Link
                href={url ?? "#"}
                className="xmd2:py-4 py-3 xmd2:px-15 xmd:font-medium font-normal rounded-sm bg-mainLightPurple  text-white text-title-sm duration-300 hover:bg-extraDarkBlue block"
              >
                {isWebsiteCategory ? t("costCalculator") : t("packageAndPrice")}
              </Link>
              <button
                onClick={openTawkToChat}
                className="xmd2:py-4 py-3 xsm:m-0 px-5 border border-white rounded-sm block xmd:font-medium font-normal bg-extraDarkBlue text-white duration-300"
              >
                {t("startChat")}
              </button>
            </div>
            <div className="text-center lg:text-start">
              <Link href={"/portfolio"} className="text-main">
                {t("exampleSites")}
              </Link>
            </div>
          </div>
          <div className="flex flex-col items-center lg:pt-15 xmd:pt-8 pt-3 mb-[45px]">
            <h3 className="sm:text-title-md text-title-sm font-semibold">
              ⚡ {t("offerEnd")}
            </h3>
            <CountDown endDate={endDate} changeHeader={setDefaultHero} />
            <div>
              <Link
                {...linkProps}
                className="xmd2:py-4 py-3 sm:px-15 px-8 text-center font-medium rounded-sm bg-mainLightPurple text-white sm:text-title-sm duration-300 hover:bg-extraDarkBlue inline-block mb-[15px]"
              >
                {t("getInNow")}
              </Link>
            </div>
            <div className="flex gap-x-3 items-center">
              <a
                href={`tel:${cleanedPhoneNumber}`}
                className="xmd2:py-4 py-3 sm:px-5 px-2 text-center font-medium rounded-sm sm:text-title-sm duration-300 inline-block"
              >
                <PhoneIcon className="inline-block w-5" /> {t("call")}
              </a>
              <Link
                {...linkProps}
                className="xmd2:py-4 py-3 sm:px-5 px-2 text-center font-medium rounded-sm sm:text-title-sm duration-300 inline-block"
              >
                <FaWhatsapp className="inline-block w-5 mr-1" />
                {t("whatsappWrite")}
              </Link>
            </div>
            <p
              className="sm:text-lg text-sm font-bold cursor-pointer mb-4"
              onClick={() => copyToClipboard(cleanPhoneNumber(phone))}
            >
              {copySuccessful ? (
                <CheckCircleIcon className="inline-block w-5 text-green-2 " />
              ) : (
                <DocumentDuplicateIcon className="inline-block w-5" />
              )}
              {multiPhoneNumber}
            </p>
          </div>
          <i
            className="cursor-pointer absolute lg:bottom-10 bottom-4 -translate-x-[50%] left-[50%]"
            onClick={scrollDown}
          >
            <span className="scroll__items">
              <Image
                width={30}
                height={50}
                alt="Home-image"
                src="/icons/check.svg"
              />
              <span className="absolute top-2 left-2 scroll__items-circle">
                <Image
                  width={14}
                  height={14}
                  className="max-w-[14px] max-h-[14px]"
                  alt="Home-image"
                  src="/icons/circle.svg"
                />
              </span>
            </span>
          </i>
        </div>
      </div>
    </div>
  ) : (
    <Loading />
  );
};
